import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Star, RefreshCw, Loader2, ArrowUpDown, Truck, CheckCircle, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function fmt(v, decimals = 1) {
  return v != null ? Number(v).toFixed(decimals) : '—';
}

function fmtPct(v) {
  return v != null ? `${(v * 100).toFixed(1)}%` : '—';
}

function fmtMoney(v) {
  return v != null ? `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—';
}

function RateBar({ value, good = 'high' }) {
  if (value == null) return <span className="text-muted-foreground text-xs">—</span>;
  const pct = Math.min(value * 100, 100);
  const isGood = good === 'high' ? value >= 0.8 : value <= 0.05;
  const cls = isGood ? 'bg-green-500' : value >= 0.5 ? 'bg-yellow-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${cls}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs">{fmtPct(value)}</span>
    </div>
  );
}

function RatingStars({ value }) {
  if (value == null) return <span className="text-muted-foreground text-xs">—</span>;
  const score = Math.round(value);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          className={`h-3 w-3 ${s <= score ? 'text-yellow-500 fill-yellow-500' : 'text-muted'}`}
        />
      ))}
      <span className="text-xs text-muted-foreground ml-1">{fmt(value)}</span>
    </div>
  );
}

export default function CarrierScorecardPage() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortCol, setSortCol] = useState('loads_assigned');
  const [sortDir, setSortDir] = useState(-1);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await window.fetch(`${API_URL}/api/admin/reports/carrier-scorecard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetch(); }, [fetch]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir((d) => -d);
    else { setSortCol(col); setSortDir(-1); }
  };

  const carriers = (data?.carriers || [])
    .filter((c) => {
      const matchSearch = !search || c.company_name.toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === 'all' || c.status === statusFilter;
      return matchSearch && matchStatus;
    })
    .sort((a, b) => {
      const av = a[sortCol], bv = b[sortCol];
      if (av == null) return 1;
      if (bv == null) return -1;
      return av < bv ? sortDir : av > bv ? -sortDir : 0;
    });

  const SortBtn = ({ col, children }) => (
    <button
      onClick={() => toggleSort(col)}
      className="flex items-center gap-1 hover:text-foreground transition-colors"
    >
      {children}
      <ArrowUpDown className={`h-3 w-3 ${sortCol === col ? 'text-accent' : 'opacity-40'}`} />
    </button>
  );

  const activeCount = (data?.carriers || []).filter((c) => c.status === 'active_verified').length;
  const totalLoads = (data?.carriers || []).reduce((a, c) => a + c.loads_assigned, 0);
  const totalDelivered = (data?.carriers || []).reduce((a, c) => a + c.loads_delivered, 0);
  const avgRating = (() => {
    const rated = (data?.carriers || []).filter((c) => c.rating_internal != null);
    if (!rated.length) return null;
    return rated.reduce((a, c) => a + c.rating_internal, 0) / rated.length;
  })();

  return (
    <div className="space-y-6" data-testid="carrier-scorecard-report">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl font-semibold flex items-center gap-2">
            <Star className="h-6 w-6 text-accent" strokeWidth={1.5} />
            Carrier Scorecard
          </h1>
          <p className="text-muted-foreground text-sm">Performance metrics across all carriers — click column headers to sort</p>
        </div>
        <Button variant="outline" size="icon" onClick={fetch} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Active Carriers', value: activeCount,              icon: CheckCircle, color: 'green' },
          { label: 'Total Carriers',  value: data?.total ?? '—',       icon: Truck,       color: ''      },
          { label: 'Loads Assigned',  value: totalLoads,               icon: Truck,       color: ''      },
          { label: 'Loads Delivered', value: totalDelivered,           icon: CheckCircle, color: 'green' },
        ].map(({ label, value, icon: Icon, color }) => (
          <Card key={label} className="bg-card border-white/5">
            <CardContent className="p-5 flex items-start justify-between">
              <div>
                <p className="text-xs text-muted-foreground mb-1">{label}</p>
                <p className={`text-2xl font-outfit font-semibold ${color === 'green' ? 'text-green-500' : ''}`}>{value}</p>
              </div>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color === 'green' ? 'bg-green-500/10' : 'bg-accent/10'}`}>
                <Icon className={`h-4 w-4 ${color === 'green' ? 'text-green-500' : 'text-accent'}`} strokeWidth={1.5} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="bg-card border-white/5">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Input
              placeholder="Search carrier..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-transparent border-border w-56"
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-44 bg-transparent border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active_verified">Active Verified</SelectItem>
                <SelectItem value="pending_verification">Pending</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground ml-auto">
              {carriers.length} carrier{carriers.length !== 1 ? 's' : ''}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Scorecard Table */}
      <Card className="bg-card border-white/5">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
          ) : carriers.length === 0 ? (
            <p className="text-center text-muted-foreground py-16">No carriers found</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-muted-foreground text-xs">
                    <th className="text-left py-3 px-4">Carrier</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="py-3 px-4 text-right"><SortBtn col="loads_assigned">Assigned</SortBtn></th>
                    <th className="py-3 px-4 text-right"><SortBtn col="loads_delivered">Delivered</SortBtn></th>
                    <th className="py-3 px-4 text-right"><SortBtn col="avg_carrier_pay">Avg Pay</SortBtn></th>
                    <th className="py-3 px-4 text-left"><SortBtn col="on_time_rate">On-Time</SortBtn></th>
                    <th className="py-3 px-4 text-left"><SortBtn col="damage_rate">Damage</SortBtn></th>
                    <th className="py-3 px-4 text-left"><SortBtn col="cancel_rate">Cancel</SortBtn></th>
                    <th className="py-3 px-4 text-left"><SortBtn col="communication_rating">Comm.</SortBtn></th>
                    <th className="py-3 px-4 text-right"><SortBtn col="rating_internal">Score</SortBtn></th>
                  </tr>
                </thead>
                <tbody>
                  {carriers.map((c) => {
                    const deliveryRate = c.loads_assigned > 0
                      ? Math.round((c.loads_delivered / c.loads_assigned) * 100)
                      : 0;
                    return (
                      <tr key={c.id} className="border-b border-white/5 hover:bg-muted/20 transition-colors">
                        <td className="py-3 px-4">
                          <p className="font-medium">{c.company_name}</p>
                          <p className="text-xs text-muted-foreground">{c.total_deliveries} lifetime deliveries</p>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={`text-xs px-2 py-0.5 border capitalize ${
                            c.status === 'active_verified'
                              ? 'bg-green-500/10 text-green-500 border-green-500/20'
                              : 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20'
                          }`}>
                            {c.status === 'active_verified' ? 'Active' : 'Pending'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right font-semibold">{c.loads_assigned}</td>
                        <td className="py-3 px-4 text-right">
                          <span className={deliveryRate >= 80 ? 'text-green-500' : deliveryRate >= 50 ? 'text-yellow-500' : 'text-red-500'}>
                            {c.loads_delivered}
                          </span>
                          <span className="text-xs text-muted-foreground ml-1">({deliveryRate}%)</span>
                        </td>
                        <td className="py-3 px-4 text-right">{fmtMoney(c.avg_carrier_pay)}</td>
                        <td className="py-3 px-4"><RateBar value={c.on_time_rate} good="high" /></td>
                        <td className="py-3 px-4"><RateBar value={c.damage_rate} good="low" /></td>
                        <td className="py-3 px-4"><RateBar value={c.cancel_rate} good="low" /></td>
                        <td className="py-3 px-4"><RatingStars value={c.communication_rating} /></td>
                        <td className="py-3 px-4 text-right">
                          <span className={`font-semibold text-base ${
                            c.rating_internal >= 80 ? 'text-green-500' :
                            c.rating_internal >= 50 ? 'text-yellow-500' : 'text-red-500'
                          }`}>
                            {c.rating_internal ?? '—'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
